// Inventory-receipts client surface (slice 4e). Lives in its own
// sibling module so the manufacturingClient entrypoint stays under the
// 300-line file-size-hygiene threshold (pattern precedent:
// `evidenceClient.ts`, `aliasesClient.ts`, `deliveryClient.ts`).
//
// Public surface:
//   - `listManufacturingReceipts(filters)` — paginated list for the
//     Receipts tab in the Inventory panel.
//   - `deleteManufacturingReceipt(receiptId)` — removes the row + the
//     storage object.
//   - `uploadManufacturingReceipt({ file, metadata })` — runs the
//     sign → browser upload → commit dance and returns the created
//     receipt row. Mirrors `uploadManufacturingStopEvidence` shape.
import { manufacturingFetch } from "@/lib/manufacturingClient";
import { requireSupabase } from "@/lib/supabase";
import type {
  MfgInventoryReceipt,
  MfgInventoryReceiptsListFilters,
  MfgInventoryReceiptsListResponse,
  MfgReceiptCommitResponse,
  MfgReceiptDeleteResponse,
  MfgReceiptDismissUnpostedResponse,
  MfgReceiptExtractResponse,
  MfgReceiptSignResponse,
  MfgReceiptsAttentionResponse,
  MfgReceiptUpdateResponse,
  MfgVendorSuggestionsResponse,
} from "@/lib/manufacturing/types/receipts";

type SignOk = MfgReceiptSignResponse;
type CommitOk = MfgReceiptCommitResponse;

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.receipt_id === "string" &&
    typeof o.plant_id === "string" &&
    typeof o.content_type === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && o.receipt !== null && typeof o.receipt === "object";
}

export function listManufacturingReceipts(
  filters: MfgInventoryReceiptsListFilters = {}
) {
  return manufacturingFetch<MfgInventoryReceiptsListResponse>("/api/mfg/receipts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(filters),
  });
}

export function deleteManufacturingReceipt(receiptId: string) {
  return manufacturingFetch<MfgReceiptDeleteResponse>("/api/mfg/receipts/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ receipt_id: receiptId }),
  });
}

/** Slice 4f: trigger (or re-trigger) OCR extraction on a receipt. Cached
 *  on the row; pass `force=true` from the Re-extract button to bypass
 *  the cache and re-bill the model call. */
export function extractManufacturingReceipt(receiptId: string, opts: { force?: boolean } = {}) {
  return manufacturingFetch<MfgReceiptExtractResponse>("/api/mfg/receipts/extract", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ receipt_id: receiptId, force: opts.force === true }),
  });
}

/** Patches whitelisted header fields on a receipt. Today vendor_name,
 *  shipping_cost, and tax_amount are editable; total_cost is
 *  intentionally not exposed because re-uploads are the canonical
 *  path for total corrections. */
export function updateManufacturingReceiptHeader(
  receiptId: string,
  patch: { vendor_name?: string; shipping_cost?: number; tax_amount?: number }
) {
  return manufacturingFetch<MfgReceiptUpdateResponse>("/api/mfg/receipts/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ receipt_id: receiptId, ...patch }),
  });
}

/** Distinct vendor names this org has used (across receipts +
 *  aliases + material metadata), usage-ranked. Drives the vendor
 *  autocomplete in the Review modal and MaterialModalForm (VEN-2b).
 *  Pass `includeAliases: true` for compact alias rows (pick existing). */
export function listManufacturingVendorSuggestions(opts?: { includeAliases?: boolean }) {
  const q = opts?.includeAliases ? "?include_aliases=1" : "";
  return manufacturingFetch<MfgVendorSuggestionsResponse>(`/api/mfg/vendors${q}`, {
    method: "GET",
  });
}

/** Slice 4g: org-wide attention summary -- count + per-row items for
 *  recent receipts. Drives the Inventory banner and the per-row status
 *  chips on the Receipts tab. Future firefly-subscriber surface (see
 *  receiptAttentionService.ts header). */
export function listManufacturingReceiptsAttention(opts?: { limit?: number }) {
  const q = opts?.limit ? `?limit=${encodeURIComponent(String(opts.limit))}` : "";
  return manufacturingFetch<MfgReceiptsAttentionResponse>(
    `/api/mfg/receipts/attention${q}`,
    { method: "GET" }
  );
}

/** Slice 4g: silences (or re-surfaces) a receipt from the attention
 *  counter. Default-true `dismissed` matches the natural button label
 *  ("Dismiss"); pass false to re-surface. */
export function dismissReceiptUnposted(receiptId: string, dismissed: boolean = true) {
  return manufacturingFetch<MfgReceiptDismissUnpostedResponse>(
    "/api/mfg/receipts/dismiss-unposted",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ receipt_id: receiptId, dismissed }),
    }
  );
}

export type ReceiptUploadMetadata = {
  vendor_name?: string;
  receipt_date: string;
  total_cost?: number;
  shipping_cost?: number;
  tax_amount?: number;
  notes?: string;
  plant_id?: string;
};

export type ReceiptUploadResult =
  | { ok: true; receipt: MfgInventoryReceipt }
  | { ok: false; error: string };

export async function uploadManufacturingReceipt(args: {
  file: File;
  metadata: ReceiptUploadMetadata;
}): Promise<ReceiptUploadResult> {
  const sb = requireSupabase();
  const contentType = (args.file.type || "application/octet-stream").toLowerCase();

  const sign = await manufacturingFetch<unknown>("/api/mfg/receipts/capture", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      plant_id: args.metadata.plant_id,
      content_type: contentType,
      byte_size: args.file.size,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    const errCandidate = (sign.data as { error?: unknown })?.error;
    return {
      ok: false,
      error:
        typeof errCandidate === "string" ? errCandidate : "Sign response was malformed.",
    };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage.from(s.bucket).uploadToSignedUrl(
    s.path,
    s.token,
    args.file,
    { contentType: s.content_type, upsert: false }
  );
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await manufacturingFetch<unknown>("/api/mfg/receipts/capture", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "commit",
      receipt_id: s.receipt_id,
      object_path: s.path,
      content_type: s.content_type,
      byte_size: args.file.size,
      plant_id: s.plant_id,
      vendor_name: args.metadata.vendor_name,
      receipt_date: args.metadata.receipt_date,
      total_cost: args.metadata.total_cost,
      shipping_cost: args.metadata.shipping_cost,
      tax_amount: args.metadata.tax_amount,
      notes: args.metadata.notes,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    const errCandidate = (commit.data as { error?: unknown })?.error;
    return {
      ok: false,
      error:
        typeof errCandidate === "string" ? errCandidate : "Commit response was malformed.",
    };
  }
  return { ok: true, receipt: commit.data.receipt };
}
